import { FC, useEffect, useState } from "react";
import { X } from "lucide-react";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/_atoms/button";
import { OwnerType } from "@/domain/_shared/lib/owner-types";

import { FileItemUploader } from "./file-item-uploader";

export interface UploadResult {
  file: File;
  url: string;
  error?: Error | undefined;
}

export interface FileUploadAction {
  id: string;
  newFile: File;
  oldFileId?: string | undefined;
  conflictHandling: "REPLACE" | "CREATE";
}

export interface FileUploaderProps {
  ownerId: string;
  ownerType: OwnerType;
  fileUploadList: FileUploadAction[];
  onFileUploadFinished?: (result: UploadResult) => void;
}

export const FileUploader: FC<FileUploaderProps> = ({ fileUploadList, ownerId, ownerType, onFileUploadFinished }) => {
  const { t } = useTranslation("common");
  const [hidden, setHidden] = useState<boolean>(false);

  useEffect(() => {
    setHidden(false);
  }, [fileUploadList]);

  async function handleFileUploadFinished(result: UploadResult) {
    if (onFileUploadFinished) {
      onFileUploadFinished(result);
    }
  }

  if (!fileUploadList || fileUploadList.length === 0) {
    return <></>;
  }

  return (
    <div className="fixed bottom-4 right-4 z-99" style={{ display: hidden ? "none" : undefined }}>
      <div className="w-80 overflow-hidden rounded-md border bg-background shadow-lg">
        <div className="flex items-center justify-between border-b px-4 py-3">
          <span className="text-sm font-medium">{t("documents.uploadDocuments")}</span>
          <Button
            variant="ghost"
            size="icon"
            className="h-auto w-auto rounded-xs p-0.5 text-muted-foreground hover:text-foreground"
            aria-label="Close"
            onClick={() => setHidden(true)}
          >
            <X className="h-3.5 w-3.5" />
          </Button>
        </div>

        <div className="flex flex-col divide-y">
          {fileUploadList.map((fileUploadAction) => (
            <FileItemUploader
              key={fileUploadAction.id}
              ownerId={ownerId}
              ownerType={ownerType}
              fileUploadAction={fileUploadAction}
              onFileUploadFinished={handleFileUploadFinished}
            />
          ))}
        </div>
      </div>
    </div>
  );
};
